// ABOUTME: Draws the persistent scenery a screen sits in (ocean, clouds, sky) from the crossing's scene alone; the breath scene is 1A's own layer.
// ABOUTME: The frame keys it by scene, so it remounts only when the scene changes; a screen's own layer is its children.

import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import type { CrossingScene, CrossingWithProgress } from '../../../types/api';
import DeckSprite from '../crossing/DeckSprite';
import OceanScene from '../crossing/OceanScene';
import ForgivenSkyLayer from '../screens/sky/ForgivenSkyLayer';
import SkyScene from '../screens/sky/SkyScene';
import { HorizonScreenContext } from '../crossing/useHorizon';
import CloudStack from './CloudStack';
import type { ScreenResult } from './screenContract';

interface SceneHostProps {
  scene: CrossingScene;
  screenId?: string; // the screen the scenery is drawn for; the horizon stands where v28 puts it for this screen
  crossing?: CrossingWithProgress; // the stored readings the cloud stack is drawn from
  cloudTiers?: ScreenResult['cloudTiers']; // the screen's live tiers, drawn in the stack
  sceneSky?: ScreenResult['sceneSky']; // the sky variant the screen asks the sky scene to draw (5C forgiven; 5D none)
  sceneSkyFadesIn?: boolean; // the forgiven sky behind the clouds fades in (4B, once forgiven)
  tiltAway?: boolean; // the sky scenery tilts back and fades out (5C's sky, as 5D builds in)
  deckBuildIn?: boolean; // the ocean scene's deck and sea rise into place (2A arriving from Ready for compass)
  stackBuildIn?: boolean; // the cloud stack builds bottom-up (2B arriving from 2A by This feels close)
  children?: ReactNode;
  inert?: boolean; // set while an overlay page covers the scenery
}

interface SceneryProps {
  scene: CrossingScene;
  sky?: ScreenResult['sceneSky'];
  skyFadesIn?: boolean;
  deckBuildIn?: boolean;
}

function Scenery({ scene, sky, skyFadesIn, deckBuildIn }: SceneryProps) {
  // 1A's layer draws the Reset scene, which it drives.
  if (scene === 'breath') return null;
  if (scene === 'ocean') {
    return (
      <>
        <OceanScene sky="feeling" buildIn={deckBuildIn} />
        <DeckSprite buildIn={deckBuildIn} />
      </>
    );
  }
  if (scene === 'clouds') {
    return (
      <>
        <OceanScene />
        {sky === 'forgiven' && <ForgivenSkyLayer fadeIn={skyFadesIn} />}
        <DeckSprite />
      </>
    );
  }
  return (
    <>
      <SkyScene forgiven={sky === 'forgiven'} summary={sky === 'summary'} />
      {sky !== 'summary' && <DeckSprite />}
    </>
  );
}

export default function SceneHost({ scene, screenId, crossing, cloudTiers, sceneSky, sceneSkyFadesIn, tiltAway, deckBuildIn, stackBuildIn, children, inert }: SceneHostProps) {
  return (
    <HorizonScreenContext.Provider value={screenId}>
    <Box data-testid="runner-scene-host" data-scene={scene} inert={inert} sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {scene === 'sky' ? (
        // The sky scenery's own parts are position: fixed, so this box is the one they resolve against and it can tilt them as one.
        <Box
          data-testid="runner-scenery"
          sx={{
            position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'none', transformOrigin: '50% 60%',
            transform: tiltAway ? 'perspective(1200px) rotateX(22deg) scale(0.86) translateY(90px)' : 'none',
            opacity: tiltAway ? 0 : 1,
            transition: 'transform 1.9s cubic-bezier(.5,0,.6,1), opacity 1.1s ease .15s',
          }}
        >
          <Scenery scene={scene} sky={sceneSky} skyFadesIn={sceneSkyFadesIn} deckBuildIn={deckBuildIn} />
        </Box>
      ) : (
        <Scenery scene={scene} sky={sceneSky} skyFadesIn={sceneSkyFadesIn} deckBuildIn={deckBuildIn} />
      )}
      {children}
      {/* After the screen's layer in the DOM, so Tab goes from the screen wrapper to the stack's arrows and then the
          plate; it is drawn over the host either way (positioned). */}
      {scene === 'clouds' && crossing && <CloudStack crossing={crossing} cloudTiers={cloudTiers} buildIn={stackBuildIn} />}
    </Box>
    </HorizonScreenContext.Provider>
  );
}
